// Shared formatting and stage logic for the tracking components. Stages come
// from the API (`shipment.stage`, see docs/tracking-plan.md §5.6); older API
// payloads without a stage fall back to the Shiprocket status label.

export const fmtDay = (iso) =>
  iso ? new Date(iso).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' }) : null

export const fmtStamp = (iso) =>
  iso ? new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true }) : null

/** "just now" / "12 min ago" / "3 h ago" / a date — how fresh the courier data is. */
export function ago(iso) {
  if (!iso) return null
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins} min ago`
  if (mins < 24 * 60) return `${Math.round(mins / 60)} h ago`
  return fmtStamp(iso)
}

/** Stage when the API sent one, else a best guess from the status label. */
export function stageOf(shipment) {
  if (shipment?.stage && shipment.stage !== 'unknown') return shipment.stage
  const s = String(shipment?.status ?? '').toUpperCase()
  if (!s) return 'created'
  if (s === 'DELIVERED') return 'delivered'
  if (s === 'RTO DELIVERED') return 'rto_delivered'
  if (s.startsWith('RTO')) return 'rto'
  if (s.startsWith('UNDELIVERED')) return 'delivery_failed'
  if (s === 'OUT FOR DELIVERY') return 'out_for_delivery'
  if (['CANCELED', 'CANCELLED'].includes(s)) return 'cancelled'
  if (['LOST', 'DAMAGED', 'DESTROYED'].includes(s)) return 'lost'
  if (s.includes('PICKUP')) return 'pickup_scheduled'
  if (s === 'NEW' || s === 'AWB ASSIGNED') return 'awb_assigned'
  return 'in_transit'
}

/** Customer-facing words and colour for each stage. */
export const STAGE_META = {
  created: { label: 'Order placed', tone: 'amber', icon: 'fa-receipt' },
  awb_assigned: { label: 'Packed', tone: 'amber', icon: 'fa-box' },
  pickup_scheduled: { label: 'Ready for pickup', tone: 'amber', icon: 'fa-box' },
  pickup_failed: { label: 'Pickup delayed', tone: 'amber', icon: 'fa-clock' },
  in_transit: { label: 'In transit', tone: 'indigo', icon: 'fa-truck-fast' },
  out_for_delivery: { label: 'Out for delivery', tone: 'indigo', icon: 'fa-person-biking' },
  delivery_failed: { label: 'Delivery attempt failed', tone: 'red', icon: 'fa-triangle-exclamation' },
  delivered: { label: 'Delivered', tone: 'green', icon: 'fa-circle-check' },
  rto: { label: 'Returning to seller', tone: 'red', icon: 'fa-rotate-left' },
  rto_delivered: { label: 'Returned to seller', tone: 'red', icon: 'fa-rotate-left' },
  cancelled: { label: 'Shipment cancelled', tone: 'red', icon: 'fa-ban' },
  lost: { label: 'Lost in transit', tone: 'red', icon: 'fa-circle-exclamation' },
  unknown: { label: 'In transit', tone: 'indigo', icon: 'fa-truck-fast' },
}

export const TONES = {
  green: 'bg-green-50 text-green-700 border-green-200',
  red: 'bg-red-50 text-red-700 border-red-200',
  amber: 'bg-amber-50 text-amber-700 border-amber-200',
  indigo: 'bg-indigo-50 text-indigo-700 border-indigo-200',
}

const TERMINAL = new Set(['delivered', 'rto_delivered', 'cancelled', 'lost'])

/**
 * How often an open tracking view re-checks (tracking-plan §8.2): every
 * minute while it's out for delivery or just failed, every 5 minutes while
 * moving, never once it's settled. The API refreshes from the courier itself
 * when its copy is stale, so this only picks those changes up.
 */
export function refreshIntervalFor(shipment) {
  if (!shipment) return false
  const stage = stageOf(shipment)
  if (TERMINAL.has(stage)) return false
  return stage === 'out_for_delivery' || stage === 'delivery_failed' ? 60_000 : 5 * 60_000
}

/** Scans newest first: the v2 `events`, else the legacy `activities` list. */
export function scansOf(shipment) {
  if (Array.isArray(shipment?.events) && shipment.events.length > 0) {
    return shipment.events.map((e) => ({ when: e.occurred_at, text: e.activity || e.label, location: e.location }))
  }
  return (Array.isArray(shipment?.activities) ? shipment.activities : []).map((a) => ({ when: a.date, text: a.activity || a.status, location: a.location }))
}

/** Plain message from an API failure: our own 4xx copy is customer-safe, anything else is generic. */
export function apiErrorMessage(err, fallback = 'Something went wrong. Please try again.') {
  const status = err?.response?.status
  const message = err?.response?.data?.message
  if (status === 429) return 'Too many attempts. Please wait a minute and try again.'
  if (status >= 400 && status < 500 && typeof message === 'string' && message && message !== 'Validation error') return message
  return fallback
}

/** YYYY-MM-DD in India time, `days` from today. */
export function istDate(days = 0) {
  return new Date(Date.now() + 5.5 * 3600_000 + days * 86_400_000).toISOString().slice(0, 10)
}
